import { execFileSync } from "node:child_process";
import { loadAutoConfig } from "../hive/auto.js";

/**
 * `aethereum check` — the ENFORCED contract gate (CAP-1). Run as a git
 * pre-commit / pre-push hook. It reads the team's current contracts (each with
 * optional file globs), matches them against the commit's staged files, and
 * FLAGS a commit that touches an interface which is:
 *   - mid-negotiation (an open proposal or a pending-approval on it), or
 *   - marked `frozen` (declared do-not-change),
 * and (v4-S11 E1) flags staged files that violate a mechanically checkable rule
 * in the team ruleset (see the grammar above parseRulesetRules).
 *
 * This is the moat: a same-vendor agent-team feature can coordinate agents inside
 * one session, but it cannot gate the git commit of a DIFFERENT teammate's
 * DIFFERENT-brand agent on a DIFFERENT machine. `aethereum check` can, because it
 * sits at the git boundary and reads shared cross-machine room state.
 *
 * FAIL-SOFT is non-negotiable (CLAUDE.md): not configured, offline, timeout, or
 * any error → print nothing blocking and exit 0. Only `--strict` (or a future
 * room setting) exits non-zero on a block; the default is advisory (prints, exits
 * 0) so Aethereum being down can never block a developer's commit.
 */

interface CheckContract {
  name: string;
  version: number;
  files?: string[];
  stability?: "stable" | "unstable" | "frozen";
}
interface FullContext {
  contracts?: CheckContract[];
  pendingProposals?: Array<{ name: string }>;
  myProposals?: Array<{ name: string }>;
  pendingApprovals?: Array<{ name: string }>;
  /** The team ruleset (CAP-5), as surfaced by `?full=1`. */
  ruleset?: { text: string } | null;
}

// ── Ruleset compliance (v4-S11 E1) ───────────────────────────────────────────
//
// The room ruleset (aethereum__set_ruleset; rides get_team_context) may contain
// mechanically checkable rules. The extractor is small and CONSERVATIVE: a line
// either matches the grammar exactly or it is IGNORED — never guessed at.
//
// Grammar (one rule per line, leading/trailing whitespace ignored):
//
//   forbid <glob>                 a staged file matching <glob> is flagged
//   require <globA> -> <globB>    when a staged file matches <globA>, at least
//                                 one staged file must also match <globB>
//   - do not commit <glob>        markdown-bullet form of forbid; "edit" also
//   * do not edit `<glob>`        accepted, bullet may be -, * or +, the glob
//                                 may be `backticked`, a trailing . is fine
//
// Globs use the same minimal syntax as contract file globs (`**`, `*`, `?`).

export interface RulesetRule {
  kind: "forbid" | "require";
  glob: string;
  /** `require` only: the glob some staged file must also match. */
  requiredGlob?: string;
  /** The exact ruleset line, quoted back to the user in a flag. */
  source: string;
}

const FORBID_RE = /^forbid\s+(\S+)$/i;
const REQUIRE_RE = /^require\s+(\S+)\s*->\s*(\S+)$/i;
const BULLET_FORBID_RE = /^[-*+]\s+do not (?:commit|edit)\s+`?([^\s`]+?)`?\.?$/i;

/** Parse the mechanically checkable rules out of a ruleset text. Unparseable lines are ignored. */
export function parseRulesetRules(text: string): RulesetRule[] {
  const rules: RulesetRule[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    let m = FORBID_RE.exec(line);
    if (m) {
      rules.push({ kind: "forbid", glob: m[1]!, source: line });
      continue;
    }
    m = REQUIRE_RE.exec(line);
    if (m) {
      rules.push({ kind: "require", glob: m[1]!, requiredGlob: m[2]!, source: line });
      continue;
    }
    m = BULLET_FORBID_RE.exec(line);
    if (m) {
      rules.push({ kind: "forbid", glob: m[1]!, source: line });
    }
    // anything else: not mechanically checkable — ignored, never guessed
  }
  return rules;
}

/** Minimal glob → RegExp: supports `**`, `*`, `?`. Anchored, `/`-aware. */
export function globToRegExp(glob: string): RegExp {
  let re = "";
  for (let i = 0; i < glob.length; i++) {
    const ch = glob[i]!;
    if (ch === "*") {
      if (glob[i + 1] === "*") {
        re += ".*"; // `**` — any path depth
        i++;
        if (glob[i + 1] === "/") i++; // consume the slash after **
      } else {
        re += "[^/]*"; // `*` — within a path segment
      }
    } else if (ch === "?") {
      re += "[^/]";
    } else if (".+^${}()|[]\\".includes(ch)) {
      re += "\\" + ch;
    } else {
      re += ch;
    }
  }
  return new RegExp(`^${re}$`);
}

export function matchesAny(file: string, globs: string[]): boolean {
  return globs.some((g) => globToRegExp(g).test(file));
}

/** Staged file paths (pre-commit). Empty on any git error. */
function stagedFiles(runGit: (args: string[]) => string): string[] {
  try {
    return runGit(["diff", "--name-only", "--cached", "--diff-filter=ACMR"])
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

export interface CheckResult {
  /** exit code the CLI should use */
  code: number;
  /** lines to print (already human-readable) */
  lines: string[];
}

/** Pure gate logic, unit-testable: given changed files + context, decide blocks. */
export function evaluateGate(
  changed: string[],
  ctx: FullContext,
  strict: boolean,
): CheckResult {
  const contracts = (ctx.contracts ?? []).filter((c) => c.files && c.files.length);
  const negotiating = new Set<string>([
    ...(ctx.pendingProposals ?? []).map((p) => p.name),
    ...(ctx.myProposals ?? []).map((p) => p.name),
    ...(ctx.pendingApprovals ?? []).map((p) => p.name),
  ]);

  const blocks: string[] = [];

  // v4-S11 E1: ruleset compliance on mechanically checkable rules (see the
  // grammar comment above parseRulesetRules). Same advisory/strict envelope.
  const rules = ctx.ruleset?.text ? parseRulesetRules(ctx.ruleset.text) : [];
  for (const rule of rules) {
    if (rule.kind === "forbid") {
      const hits = changed.filter((f) => matchesAny(f, [rule.glob]));
      if (hits.length) {
        blocks.push(
          `✗ team ruleset says "${rule.source}" and your commit touches ${hits.join(", ")}.\n` +
            `  Change the commit, or update the room ruleset (aethereum__set_ruleset) if the rule is stale.`,
        );
      }
    } else {
      const triggers = changed.filter((f) => matchesAny(f, [rule.glob]));
      if (triggers.length && !changed.some((f) => matchesAny(f, [rule.requiredGlob!]))) {
        blocks.push(
          `✗ team ruleset says "${rule.source}": ${triggers.join(", ")} matches the left side but nothing staged matches ${rule.requiredGlob}.\n` +
            `  Stage a matching file too, or update the room ruleset (aethereum__set_ruleset) if the rule is stale.`,
        );
      }
    }
  }

  for (const c of contracts) {
    const touched = changed.filter((f) => matchesAny(f, c.files!));
    if (!touched.length) continue;
    // v4-S1 G1 contract-conformance would slot here once the conformance field on contracts lands (it has not).
    if (negotiating.has(c.name)) {
      blocks.push(
        `✗ "${c.name}" is mid-negotiation (an open proposal is pending) and your commit touches ${touched.join(", ")}.\n` +
          `  Resolve it first: aethereum__respond_to_proposal / finalize_proposal, then re-commit.`,
      );
    } else if (c.stability === "frozen") {
      blocks.push(
        `✗ "${c.name}" is marked FROZEN (do not change) and your commit touches ${touched.join(", ")}.\n` +
          `  If this change is intended, propose it: aethereum__propose_contract "${c.name}" <newShape>.`,
      );
    }
  }

  if (!blocks.length) return { code: 0, lines: [] };
  const header = strict
    ? "Aethereum: blocking this commit, it breaks a team rule or touches a contract that isn't safe to change yet:"
    : "Aethereum (advisory): this commit breaks a team rule or touches a contract that isn't safe to change yet:";
  const footer = strict
    ? "\nCommit blocked (--strict). Fix the above, or bypass once with `git commit --no-verify`."
    : "\nThis is advisory (commit allowed). Run with --strict to enforce.";
  return { code: strict ? 1 : 0, lines: [header, "", ...blocks, footer] };
}

export interface CheckOptions {
  strict?: boolean;
  cwd?: string;
  runGit?: (args: string[]) => string;
  fetchImpl?: typeof fetch;
}

export async function runCheck(opts: CheckOptions = {}): Promise<number> {
  const cwd = opts.cwd ?? process.cwd();
  const runGit =
    opts.runGit ?? ((args: string[]) => execFileSync("git", args, { cwd, encoding: "utf8" }));
  const fetchImpl = opts.fetchImpl ?? fetch;

  const cfg = loadAutoConfig(cwd, process.env as Record<string, string | undefined>);
  if (!cfg) return 0; // not configured — never block

  const changed = stagedFiles(runGit);
  if (!changed.length) return 0; // nothing staged — nothing to check

  let ctx: FullContext | null = null;
  try {
    // Measured production latency on `&full=1` (the richer, non-consuming read
    // this gate needs) is ~2.0-2.5s — a 2000ms abort meant this, the flagship
    // enforcement feature, was silently fail-soft no-op-ing almost every run.
    // 4000ms leaves real margin; this only runs at commit time, not per-keystroke,
    // so the extra latency is an acceptable trade for the gate actually firing.
    const res = await fetchImpl(`${cfg.url}/api/sync/context?peek=1&full=1`, {
      headers: { Authorization: `Bearer ${cfg.token}` },
      signal: AbortSignal.timeout(4_000),
    });
    if (res.ok) ctx = ((await res.json()) as { context?: FullContext }).context ?? null;
  } catch {
    ctx = null; // offline / timeout — fail soft
  }
  if (!ctx) return 0; // couldn't read room state — never block on Aethereum being down

  const result = evaluateGate(changed, ctx, opts.strict ?? false);
  for (const line of result.lines) process.stderr.write(line + "\n");
  return result.code;
}
